import React, { useContext, useRef, useEffect } from 'react';
import _merge from 'lodash/merge';
import classnames from 'classnames';
import { getContext, useCombinedRefs } from 'common';
import Tooltip from 'components/Tooltip/Tooltip';
import { ListContext, ListItemText, ListItemIcon } from 'components/List';
import { SelectItemBasicContext } from './SelectItemBasic';
import { SelectItemSearchableContext } from './SelectItemSearchable';
import { CONSTANTS } from '../common/ListKeyboardNav';

import Styled from './styles';

const {
  LIST_ITEM_TABABLE_ITEM,
  LIST_ITEM_SELECTED,
} = CONSTANTS;

const DEFAULT_TOOLTIP_PROPS = {
  placement: 'bottom' as const,
  fallbackPlacements: ['top', 'left', 'right'],
  trigger: ['hover' as const, 'focus' as const],
  modifiers: { offset: { offset: '0, -4' } },
  tooltip: '',
};
const SelectListItemContext = React.createContext<any>({});

export interface SelectListItemProps extends React.ComponentPropsWithRef<'div'> {
  /** If `true`, display the list item as disabled. */
  disabled?: boolean;
  /** If `true`, display the list item as selected. */
  selected?: boolean;
  /** If `true`, sets this item in the list as the one to select when using searchable `SelectItem`. */
  searchSelection?: boolean;
  /** Object indicating styles to be used for the ListItem. */
  style?: React.CSSProperties;
  /** Children, displayed as one item in the dropdown list. Consider using the Icon, SelectItemText components and/or plain text. */
  children?: React.ReactNode;
  /** If `true`, focus this item in the dropdown list (when this item is the item that is scrolled to or selected). Useful during keyboard
   * navigation of a virtualized list. */
  focusItem?: boolean;
  /** Display an optional tooltip. See Tooltip component documentation for full list of properties. */
  tooltipProps?: {
    /** The content of the Tooltip. */
    tooltip: React.ReactNode,
    /** If `true`, disables tooltip from triggering. */
    disable?: boolean
  };
  /** @ignore */
  onClick?: React.MouseEventHandler;
  /** @ignore */
  onKeyDown?: React.KeyboardEventHandler;
}

/** SelectItem menu item which supports text, icon and other elements.
 * Any other props will be provided to the root element.
 */
const SelectListItem = React.forwardRef<HTMLElement, SelectListItemProps>((props: SelectListItemProps, forwardedRef) => getContext(({ RTL, theme }) => {
  const {
    children,
    disabled,
    selected,
    searchSelection,
    onClick,
    onKeyDown,
    style,
    focusItem,
    tooltipProps,
    ...otherProps
  } = props;
  const { disable: tooltipPropsDisable, ...restOfTooltipProps } = tooltipProps;
  const context = useContext(ListContext);
  const innerRef = useRef(null);
  const ref = useCombinedRefs(forwardedRef, innerRef);
  const selectItemBasicContext = useContext(SelectItemBasicContext);
  const selectItemSearchableContext = useContext(SelectItemSearchableContext);

  useEffect(() => {
    if (focusItem === true) {
      innerRef && innerRef.current && innerRef.current.focus();
    }
  }, [focusItem]);

  const handleListItemClick = (event) => {
    if (disabled) {
      event.stopPropagation();
      event.preventDefault();
      return;
    }
    event.persist();
    onClick && onClick(event);
  };

  const handleListItemKeyDown = (event) => {
    if (disabled) {
      event.stopPropagation();
      event.preventDefault();
      return;
    }
    event.persist();
    onKeyDown && onKeyDown(event);
  };

  const isText = child => child && typeof child === 'string';
  const isIcon = child => (child && child.type && child.type.displayName === 'Icon');

  const listTabableName = context.uniqueListId ?
    `${LIST_ITEM_TABABLE_ITEM}-${context.uniqueListId}` : LIST_ITEM_TABABLE_ITEM;
  const classes = classnames({
    [listTabableName]: !disabled,
    [LIST_ITEM_SELECTED]: !disabled && selected,
  });

  const resolvedGlobalTheme = theme && theme.LIST && theme.LIST.VERTICAL;
  const resolvedUserBasicTheme = selectItemBasicContext && selectItemBasicContext.resolvedListTheme && selectItemBasicContext.resolvedListTheme.LIST && selectItemBasicContext.resolvedListTheme.LIST.VERTICAL;
  const resolvedUserSearchableTheme = selectItemSearchableContext && selectItemSearchableContext.resolvedListTheme && selectItemSearchableContext.resolvedListTheme.LIST && selectItemSearchableContext.resolvedListTheme.LIST.VERTICAL;
  const resolvedTheme = _merge({}, resolvedGlobalTheme, resolvedUserBasicTheme, resolvedUserSearchableTheme);
  const content = (
    <Styled.SelectListItem
      className={classes}
      aria-disabled={disabled}
      ref={ref}
      RTL={RTL}
      $theme={resolvedTheme}
      style={style}
      tabIndex={-1}
      disabled={disabled}
      variant={context.variant}
      selected={selected}
      aria-selected={selected}
      searchSelection={searchSelection}
      hoverEffect
      onClick={handleListItemClick}
      onKeyDown={handleListItemKeyDown}
      onFocus={event => event.preventDefault()}
      {...otherProps}
    >
      <SelectListItemContext.Provider value={{ disabled }}>
        <React.Fragment>
          {React.Children.map(props.children, (root) => {
            if (isText(root)) {
              return (<ListItemText $theme={resolvedTheme} disabled={disabled}>{root}</ListItemText>);
            } else if (root !== null && isIcon(root)) {
              return (<ListItemIcon $theme={resolvedTheme} disabled={disabled}>{root}</ListItemIcon>);
            }
            return root;
          })}
        </React.Fragment>
      </SelectListItemContext.Provider>
    </Styled.SelectListItem>
  );

  return tooltipPropsDisable ?
    content
    :
    (
      <Tooltip
        {...DEFAULT_TOOLTIP_PROPS}
        {...restOfTooltipProps}
      >
        {content}
      </Tooltip>
    );
}));

SelectListItem.displayName = 'SelectListItem';

SelectListItem.defaultProps = {
  disabled: false,
  selected: false,
  searchSelection: false,
  style: undefined,
  children: undefined,
  focusItem: false,
  tooltipProps: {
    disable: true,
    tooltip: '',
  },
  onClick: undefined,
  onKeyDown: undefined
};

export { SelectListItem as default, SelectListItemContext };